import { BoxesLine } from "@assets/icons/BoxesLine";
import { useI18nContext } from "@i18n/i18n-react";
import { cn } from "@shadcn/lib/utils";
import { Badge } from "@shadcn/ui/badge";
import { Lock, Package, ShoppingBag, ShoppingBasket, Ticket } from "lucide-react";
import { useMemo } from "react";
import type { HubService } from "./__generated__/HubMapQuery.graphql";

const SERVICE_META = {
	INSTANT_BUY: { labelKey: "INSTANT_BUY_TITLE", Icon: ShoppingBasket },
	SHOP_N_COLLECT: { labelKey: "SHOP_N_COLLECT_TITLE", Icon: ShoppingBag },
	CLICK_N_COLLECT: { labelKey: "CLICK_AND_COLLECT_TITLE", Icon: Package },
	ONEDELIVERY: { labelKey: "ONE_DELIVERY_TITLE", Icon: BoxesLine },
	RAFFLE: { labelKey: "RAFFLE_TITLE", Icon: Ticket },
	LOCK_B2B: { labelKey: "SWAP", Icon: Lock },
	LOCK_B2C: { labelKey: "LOCK", Icon: Lock },
} as const;

type ServiceName = Extract<HubService, keyof typeof SERVICE_META>;
type DisplayServiceName = keyof typeof SERVICE_META;
type HubSelectionCardHub = {
	status?: string | null;
	sncProducts?: {
		totalCount: number;
	} | null;
	meta: {
		name: string;
		street?: string | null;
		postalCode?: string | null;
		city?: string | null;
		country?: string | null;
		services?: readonly HubService[] | null;
		lat: number;
		lng: number;
	};
	distance?: number;
};

type HubSelectionCardProps = {
	action: React.ReactNode;
	hub: HubSelectionCardHub;
};

const formatAddress = (hub: { street?: string | null; postalCode?: string | null; city?: string | null; country?: string | null }) => {
	const locality = [hub.postalCode, hub.city].filter(Boolean).join(" ");

	if (hub.street && locality) {
		return `${hub.street}, ${locality}`;
	}

	return hub.street ?? locality;
};

const formatDistance = (distance?: number) => {
	if (distance == null) {
		return null;
	}

	if (distance < 1000) {
		return `${Math.round(distance)} m`;
	}

	const kilometers = distance / 1000;

	if (kilometers < 10) {
		return `${kilometers.toFixed(1)} km`;
	}

	return `${Math.round(kilometers)} km`;
};

export const HubSelectionCard = (props: HubSelectionCardProps) => {
	const { action } = props;
	const i18n = useI18nContext().LL["new-ui"];
	const hub = props.hub as unknown as HubSelectionCardHub;
	const isOnline = hub.status === "ONLINE";

	const services = useMemo(() => {
		const displayServices: DisplayServiceName[] = (hub.meta.services ?? []).filter(
			(service): service is ServiceName => service in SERVICE_META
		);

		const hasExplicitShopNCollect = (hub.meta.services ?? []).includes("SHOP_N_COLLECT");

		if ((hub.sncProducts?.totalCount ?? 0) > 0 && !hasExplicitShopNCollect && !displayServices.includes("SHOP_N_COLLECT")) {
			const clickAndCollectIndex = displayServices.indexOf("CLICK_N_COLLECT");
			displayServices.splice(clickAndCollectIndex >= 0 ? clickAndCollectIndex : displayServices.length, 0, "SHOP_N_COLLECT");
		}

		const seenLabels = new Set<string>();

		return displayServices
			.filter((service) => {
				const meta = SERVICE_META[service];
				const label = i18n[meta.labelKey]();
				if (seenLabels.has(label)) {
					return false;
				}
				seenLabels.add(label);
				return true;
			})
			.map((service) => {
				const meta = SERVICE_META[service];
				return {
					key: service,
					label: i18n[meta.labelKey](),
					Icon: meta.Icon,
				};
			});
	}, [hub.meta.services, hub.sncProducts?.totalCount, i18n]);
	const address = formatAddress(hub.meta);
	const mapsHref = `https://www.google.com/maps/dir/?api=1&destination=${hub.meta.lat},${hub.meta.lng}`;

	return (
		<div className="flex h-full w-full flex-col overflow-hidden rounded-xl border bg-card shadow-xl">
			<div className="flex flex-1 flex-col gap-6 px-6 pt-6">
				<div className="flex items-start justify-between gap-4">
					<div className="min-w-0">
						<h3 className="font-breit text-2xl font-medium leading-8 text-card-foreground">{hub.meta.name.toUpperCase()} Hub</h3>
					</div>
					<Badge variant="default" className="rounded-md px-2 py-0.5 text-xs leading-4">
						{formatDistance(hub.distance)}
					</Badge>
				</div>

				<div className="space-y-2">
					{address ? (
						<a
							href={mapsHref}
							target="_blank"
							rel="noreferrer"
							className="text-sm leading-5 text-muted-foreground underline underline-offset-2"
						>
							{address}
						</a>
					) : null}

					<div className="flex items-center gap-1.5 text-sm leading-5 text-muted-foreground">
						<span className={cn("size-2 rounded-full", isOnline ? "bg-green-600" : "bg-red-600")} />
						<span>{isOnline ? `${i18n.OPEN_24H()} - ${i18n.EVERYDAY()}` : i18n.TEMPORARILY_OFFLINE()}</span>
					</div>
				</div>

				{services.length > 0 ? (
					<div>
						<p className="mb-2 font-breit text-xs leading-4 font-medium text-foreground">{i18n.BACE_SERVICES()}</p>
						<div className="flex flex-wrap gap-2">
							{services.map(({ key, label, Icon }) => (
								<Badge
									key={key}
									variant="secondary"
									className="rounded-md px-2 py-0.5 text-xs leading-4 font-medium text-secondary-foreground"
								>
									<Icon className="size-3 shrink-0 stroke-[1.25]" />
									{label}
								</Badge>
							))}
						</div>
					</div>
				) : null}
			</div>

			<div className="px-6 pb-6 pt-6 [&_a]:font-breit [&_a]:text-sm [&_a]:font-normal [&_a]:leading-5 [&_a]:text-primary-foreground [&_button]:font-breit [&_button]:text-sm [&_button]:font-normal [&_button]:leading-5 [&_button]:text-primary-foreground">
				{action}
			</div>
		</div>
	);
};
